/**
 * Player-color math — the CHOOSE YOUR COLOR strip and the 1B custom field
 * (hue × lightness in oklch), ported as an ALGORITHM from the two shells
 * (`design/Games Tonight Mobile.dc.html` `okLin`…`ccCrew`; the desktop shell
 * carries the byte-same block) per DESIGN-CHANGELOG 2026-08-18's four picker
 * entries. The changelog's measured invariants are this module's unit tests:
 * corner purity #FD130F / #28FF0B / #0029FC, worst adjacent-pixel step ≤ the
 * mirrors' bound, marker hue within a degree of the paint beneath it.
 *
 * WHY THE FIELD IS BUILT THIS WAY (the four entries, compressed):
 *  - Chroma is the most a hue can hold, so the field rides the sRGB boundary
 *    and the pure primaries are IN it — they are gamut corners and exist only
 *    where chroma is maxed.
 *  - But the raw boundary creases where the clipping channel changes, and a
 *    bounded ternary search (L .20–.99) is needed because below that the gamut
 *    tolerance passes near-black chroma and invents a bogus dark apex — that
 *    discontinuity WAS the blue seam. So chroma ramps black → apex → white,
 *    the two ramps joined by a polynomial soft-min (a hard `min` leaves a Mach
 *    band along the fold), and the apex LIGHTNESS is Gaussian-smoothed across
 *    hue (σ 4°) because two local maxima genuinely trade places near h264.
 *  - The hue AXIS is spaced by oklab arc length, not degrees: the angle races
 *    through the blue corner, so a degree-spaced axis packs all that change
 *    into one column and draws a wall. Every column is still a real color.
 *  - The remaining one-pixel creases are dissolved by one centred 1.4px blur
 *    of the PAINT only (the canvas component's job, not this module's) — the
 *    committed value is always the exact function of where you pressed.
 *
 * The 20-tile palette is the house strip: the player-hue formula at 18° steps,
 * in hue order so the strip reads as one ramp. Colors are NOT exclusive — the
 * ALREADY WORN row is information, not a lock — so there is no (crew,hue)
 * uniqueness anywhere (the entry's same-day amendment reversed it; X.md C2).
 *
 * Everything here is pure and cached at module level: the apex table is 360
 * ternary searches over a 14-step bisection each, built once per session, and
 * the arc-length table integrates 1440 segments once. `fieldHex` alone is what
 * the 224² canvas paint calls ~50k times.
 */

/** The field's lightness range. `L_MIN` .34 keeps the dark edge committable —
 * adaptive ink (`inkOn`) is what makes it legible, not a fence. */
export const L_MIN = 0.34;
export const L_MAX = 0.92;

/** The 20 palette hues, 18° apart in hue order, named. The names are copy —
 * they appear in tooltips (`SCARLET — ALSO DAVE`) and the PICKED row. */
export const HUE_NAMES = [
  'SCARLET',
  'EMBER',
  'ORANGE',
  'AMBER',
  'GOLD',
  'CITRON',
  'LIME',
  'CLOVER',
  'JADE',
  'SPEARMINT',
  'TEAL',
  'CYAN',
  'SKY',
  'AZURE',
  'COBALT',
  'INDIGO',
  'VIOLET',
  'ORCHID',
  'MAGENTA',
  'ROSE',
] as const;

export interface PalEntry {
  /** hue in degrees — index × 18 */
  h: number;
  /** display name, UPPERCASE copy */
  n: string;
}

export const PAL: readonly PalEntry[] = HUE_NAMES.map((n, i) => ({ h: i * 18, n }));

/**
 * The palette entry a member's hue claims, or `null` when none is within 9°.
 * Seeded hues are off the 18° grid on purpose; every one maps cleanly onto a
 * single entry (CHRIS 250→COBALT, DAVE 40→ORANGE, MIKE 200→CYAN,
 * JEFF 320→MAGENTA, CHELSEA 90→CITRON). Angular distance is wrapped.
 */
export function nearestPalHue(h: number): number | null {
  let best: PalEntry | null = null;
  let d = 999;
  for (const p of PAL) {
    const g = Math.abs(((h - p.h + 540) % 360) - 180);
    if (g < d) {
      d = g;
      best = p;
    }
  }
  return d <= 9 && best ? best.h : null;
}

// ---------------------------------------------------------------------------
// oklch ↔ sRGB — the standard matrices, written out so nothing here depends
// on a color library the repo does not carry
// ---------------------------------------------------------------------------

function okLin(L: number, C: number, h: number): [number, number, number] {
  const a = C * Math.cos((h * Math.PI) / 180);
  const b = C * Math.sin((h * Math.PI) / 180);
  const l3 = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m3 = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s3 = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  return [
    4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3,
    -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3,
    -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3,
  ];
}

/** oklch → sRGB bytes, clamped. */
export function okRgb(L: number, C: number, h: number): [number, number, number] {
  const out = okLin(L, C, h).map((x) => {
    const c = x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(Math.max(x, 0), 1 / 2.4) - 0.055;
    return Math.round(Math.min(1, Math.max(0, c)) * 255);
  });
  return [out[0]!, out[1]!, out[2]!];
}

/** oklch → `#rrggbb`. */
export function okHex(L: number, C: number, h: number): string {
  return '#' + okRgb(L, C, h).map((x) => ('0' + x.toString(16)).slice(-2)).join('');
}

/**
 * Allocation-free in-gamut test — the boundary searches run it ~500k times per
 * field paint, so it early-exits per channel and never builds an array. The
 * ±0.002 tolerance is the shells'; tightening it re-opens the bogus-dark-apex
 * hole the L .20 floor exists to close.
 */
function inG(L: number, C: number, h: number): boolean {
  const a = C * Math.cos((h * Math.PI) / 180);
  const b = C * Math.sin((h * Math.PI) / 180);
  const l3 = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m3 = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s3 = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);
  const r = 4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
  if (r < -0.002 || r > 1.002) return false;
  const g = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
  if (g < -0.002 || g > 1.002) return false;
  const bl = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3;
  return bl >= -0.002 && bl <= 1.002;
}

/** The most chroma this hue can hold at this lightness — the sRGB boundary,
 * by 14-step bisection. */
export function maxC(L: number, h: number): number {
  let lo = 0;
  let hi = 0.45;
  for (let i = 0; i < 14; i++) {
    const m = (lo + hi) / 2;
    if (inG(L, m, h)) lo = m;
    else hi = m;
  }
  return lo;
}

/** `#rrggbb` (or sRGB bytes) → oklch. Used to plot a stored custom color back
 * onto the field at its true coordinates. */
export function rgbToOk(rgb: readonly [number, number, number]): { L: number; C: number; h: number } {
  const lin = rgb.map((v) => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * lin[0] + 0.5363325363 * lin[1] + 0.0514459929 * lin[2]);
  const m = Math.cbrt(0.2119034982 * lin[0] + 0.6806995451 * lin[1] + 0.1073969566 * lin[2]);
  const s = Math.cbrt(0.0883024619 * lin[0] + 0.2817188376 * lin[1] + 0.6299787005 * lin[2]);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { L, C: Math.hypot(A, B), h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360 };
}

export function hexToOk(hex: string): { L: number; C: number; h: number } | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const b = m[1]!;
  return rgbToOk([parseInt(b.slice(0, 2), 16), parseInt(b.slice(2, 4), 16), parseInt(b.slice(4, 6), 16)]);
}

// ---------------------------------------------------------------------------
// the apex table and the arc-length hue axis
// ---------------------------------------------------------------------------

interface Apex {
  L: number;
  C: number;
}

let _apex: Apex[] | null = null;

/**
 * For each whole hue, the lightness/chroma of its purest point. The ternary
 * search is valid because chroma-vs-lightness along one hue is unimodal; it is
 * BOUNDED to L .20–.99 because below that the gamut tolerance lets near-black
 * chroma pass and resolves a bogus apex at L≈.06 — the jump between
 * neighbouring hues' apexes was the hard blue line (DESIGN-CHANGELOG
 * 2026-08-18, "the blue seam was a bogus dark apex"). The apex LIGHTNESS is
 * then Gaussian-smoothed across hue (σ 4°, ±12° window); chroma is not, so a
 * smoothed hue still reaches its own boundary via the `maxC` clamp downstream.
 * Smoothing costs pure blue a little depth (#0000FF → a rich #0027EF); red
 * and green are untouched to the eye.
 */
export function apex(h: number): Apex {
  if (!_apex) {
    const raw: Apex[] = [];
    for (let d = 0; d < 360; d++) {
      let a = 0.2;
      let b = 0.99;
      for (let i = 0; i < 26; i++) {
        const m1 = a + (b - a) / 3;
        const m2 = b - (b - a) / 3;
        if (maxC(m1, d) < maxC(m2, d)) a = m1;
        else b = m2;
      }
      const L = (a + b) / 2;
      raw.push({ L, C: maxC(L, d) });
    }
    _apex = raw.map((_, i) => {
      let s = 0;
      let w = 0;
      for (let d = -12; d <= 12; d++) {
        const k = Math.exp(-(d * d) / 32);
        s += raw[(i + d + 360) % 360]!.L * k;
        w += k;
      }
      return { L: s / w, C: raw[i]!.C };
    });
  }
  return _apex[Math.round(h) % 360]!;
}

interface HueTab {
  N: number;
  cum: number[];
  total: number;
}

let _hueTab: HueTab | null = null;

/**
 * The hue axis is spaced by perceptual distance, not by degrees: in oklch the
 * hue ANGLE races through the blue corner — a couple of degrees there cover as
 * much visible change as ten degrees of green — so a degree-spaced axis packs
 * that change into one column and draws a wall. This walks the vivid path
 * (each hue's apex, in oklab) once, measures distance along it, and hands out
 * x by arc length. Fixed the mirrors' measured worst adjacent-pixel step from
 * 104 to ~32 against a median of 4.
 */
function hueTab(): HueTab {
  if (_hueTab) return _hueTab;
  const N = 1440;
  const cum = [0];
  const pt = (h: number): [number, number, number] => {
    const c = apex(h);
    return [c.L, c.C * Math.cos((h * Math.PI) / 180), c.C * Math.sin((h * Math.PI) / 180)];
  };
  for (let i = 1; i <= N; i++) {
    const a = pt(((i - 1) / N) * 360);
    const b = pt((i / N) * 360);
    cum.push(cum[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]));
  }
  _hueTab = { N, cum, total: cum[N]! };
  return _hueTab;
}

/** x ∈ [0,1] → hue in degrees, by inverse arc length (binary search). */
export function hueOf(u: number): number {
  const t = hueTab();
  const target = Math.min(1, Math.max(0, u)) * t.total;
  let lo = 0;
  let hi = t.N;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (t.cum[m]! < target) lo = m;
    else hi = m;
  }
  const f = (target - t.cum[lo]!) / (t.cum[hi]! - t.cum[lo]! || 1);
  return ((lo + f) / t.N) * 360;
}

/** hue in degrees → x ∈ [0,1]. `hueOf`'s inverse — crew markers and the picked
 * value go through the same mapping, so a marker sits on its own hue. */
export function uOfHue(h: number): number {
  const t = hueTab();
  const i = Math.round(((((h % 360) + 360) % 360) / 360) * t.N);
  return t.cum[Math.min(t.N, Math.max(0, i))]! / t.total;
}

// ---------------------------------------------------------------------------
// the field itself
// ---------------------------------------------------------------------------

/**
 * Field coordinates → oklch. Chroma ramps black → apex → white instead of
 * tracing the boundary: the boundary creases where the clipping channel
 * changes (that drew the seam through the deep purples); the ramp is smooth in
 * both axes and still passes through each hue's purest color. The two straight
 * ramps join through a polynomial soft-min — a hard `min` leaves a slope
 * discontinuity along the fold `L = apex.L` the eye reads as a Mach band (the
 * green/cyan line). The soft-min is ≤ both ramps, so with the final `maxC`
 * clamp the result stays inside sRGB; it costs the apex only k/4 (~0.008) of
 * chroma.
 */
export function fieldHL(u: number, v: number): { h: number; L: number; C: number } {
  const h = hueOf(u);
  const L = L_MIN + (1 - v) * (L_MAX - L_MIN);
  const c = apex(h);
  const k = 0.03;
  const a = c.C * (L / c.L);
  const b = c.C * ((1 - L) / (1 - c.L));
  const t = Math.min(1, Math.max(0, 0.5 + (0.5 * (b - a)) / k));
  const C = a * t + b * (1 - t) - k * t * (1 - t);
  return { h, L, C: Math.max(0, Math.min(C, maxC(L, h))) };
}

/** Field coordinates → the committed `#rrggbb`. The paint and the value share
 * this function — what you see is what you get. */
export function fieldHex(u: number, v: number): string {
  const c = fieldHL(u, v);
  return okHex(c.L, c.C, c.h);
}

/**
 * A stored color's field coordinates: x by the shared hue axis, y by the
 * shared lightness ramp. Exact for anything the field can make; a house-
 * palette color sits at the right hue and lightness but is quieter than the
 * paint under it (`MUTED` in its tooltip), and an out-of-range lightness
 * clamps to the edge (`CLAMPED TO THE EDGE`).
 */
export function fieldXY(ok: { L: number; h: number }): { x: number; y: number } {
  return {
    x: uOfHue(ok.h),
    y: 1 - (Math.min(L_MAX, Math.max(L_MIN, ok.L)) - L_MIN) / (L_MAX - L_MIN),
  };
}

// ---------------------------------------------------------------------------
// ink
// ---------------------------------------------------------------------------

/**
 * Ink on a player color — `design/gt-logic.js#inkOn`, ported by evaluation.
 * Player colors are no longer one lightness (the field reaches the dark edge
 * of sRGB), so a hard-coded dark initial goes invisible on a deep pick: the
 * old `--gt-ink`-always measured 1.7:1 on a deep custom color; the same pick
 * now measures 10.4:1. Reads the two forms the app produces —
 * `oklch(L C h)` and `#rrggbb` — and returns the token that actually reads:
 * dark ink where it clears ~4.5:1, otherwise the light one. oklch L is ~the
 * cube root of luminance, hence L³; the hex arm is exact sRGB relative
 * luminance. Y ≥ 0.19 is the shells' threshold, kept verbatim.
 */
export function inkOn(bg: string): string {
  const s = String(bg || '');
  let Y = 0.5;
  const ok = /oklch\(\s*([\d.]+)/.exec(s);
  if (ok) {
    Y = Math.pow(+ok[1]!, 3);
  } else {
    const hx = /^#([0-9a-f]{6})$/i.exec(s.trim());
    if (hx) {
      const v = [0, 2, 4]
        .map((i) => parseInt(hx[1]!.slice(i, i + 2), 16) / 255)
        .map((x) => (x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)));
      Y = 0.2126 * v[0]! + 0.7152 * v[1]! + 0.0722 * v[2]!;
    }
  }
  return Y >= 0.19 ? 'var(--gt-ink)' : 'var(--gt-text)';
}

/** Test seam — drops the module caches so a suite can time a cold build. */
export function resetColorCaches(): void {
  _apex = null;
  _hueTab = null;
}
